import { useEffect, useMemo, useState } from "react";
import { api, API_URL } from "@/lib/api";
import { useSession } from "@/lib/session";

type LiveToken = {
  id: number;
  name: string;
  api_key_masked: string;
  api_key?: string;
  active: number;
  created_at: string;
};

type EndpointDoc = {
  id: string;
  title: string;
  method: string;
  path: string;
  summary: string;
  body?: string;
  curl: string;
};

export default function DevelopersPage() {
  const { user } = useSession();
  const base = API_URL || window.location.origin;
  const [token, setToken] = useState<LiveToken | null>(null);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const sample = "nrn_YOUR_API_TOKEN";

  async function reload() {
    const row = await api<LiveToken | null>("/settings/api-keys").catch(() => null);
    setToken(row && typeof row === "object" && "id" in row ? row : null);
    setRevealed(null);
  }

  useEffect(() => {
    if (!isAdmin) return;
    void reload().catch((e) => setError(e instanceof Error ? e.message : "Load failed"));
  }, [isAdmin]);

  const docs: EndpointDoc[] = useMemo(
    () => [
      {
        id: "click-to-call",
        title: "Click to call",
        method: "POST",
        path: "/api/pbx/calls",
        summary:
          "Start an outbound call. Pass the agent’s PBX extension — that desk auto-answers, then the customer number rings.",
        body: `{
  "phoneNumber": "7002695990",
  "extension": "1001"
}`,
        curl: `curl -X POST ${base}/api/pbx/calls \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: ${sample}" \\
  -d '{"phoneNumber":"7002695990","extension":"1001"}'`,
      },
      {
        id: "agents",
        title: "List agents",
        method: "GET",
        path: "/api/users",
        summary: "Agents with mapped extension numbers. Use extension in click-to-call.",
        curl: `curl ${base}/api/users \\
  -H "X-Api-Key: ${sample}"`,
      },
      {
        id: "extension",
        title: "Extension status",
        method: "GET",
        path: "/api/pbx/extensions/:extension",
        summary: "Line status (idle / ringing / inuse).",
        curl: `curl ${base}/api/pbx/extensions/1001 \\
  -H "X-Api-Key: ${sample}"`,
      },
      {
        id: "call-status",
        title: "Call status",
        method: "GET",
        path: "/api/pbx/calls/:id",
        summary: "Poll call state after click-to-call.",
        curl: `curl ${base}/api/pbx/calls/123 \\
  -H "X-Api-Key: ${sample}"`,
      },
      {
        id: "hangup",
        title: "Hang up",
        method: "POST",
        path: "/api/pbx/calls/:id/hangup",
        summary: "End an active call by id.",
        curl: `curl -X POST ${base}/api/pbx/calls/123/hangup \\
  -H "X-Api-Key: ${sample}"`,
      },
      {
        id: "screen-pop",
        title: "Incoming call screen-pop",
        method: "POST",
        path: "/api/v1/crm/screen-pop",
        summary:
          "Show an incoming call on the CRM user screen. Resolves {name} from CRM lookup URL / Contacts / mappings, then POSTs the payload to your CRM webhook.",
        body: `{
  "phone": "9876543210",
  "extension": "1001",
  "name": "optional — else fetched from CRM DB",
  "call_id": "optional",
  "notify": true
}`,
        curl: `curl -X POST ${base}/api/v1/crm/screen-pop \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: ${sample}" \\
  -d '{"phone":"9876543210","extension":"1001"}'`,
      },
      {
        id: "caller-lookup",
        title: "Caller name lookup",
        method: "GET",
        path: "/api/v1/crm/caller",
        summary:
          "Resolve {name} for a phone number from CRM DB lookup, Contacts, or inbound mappings. Use for preview before screen-pop.",
        curl: `curl "${base}/api/v1/crm/caller?phone=9876543210" \\
  -H "X-Api-Key: ${sample}"`,
      },
      {
        id: "receive",
        title: "Inbound receive (alias)",
        method: "POST",
        path: "/api/v1/call",
        summary:
          "Partner alias for inbound screen-pop. Same {name} resolution as /api/v1/crm/screen-pop.",
        body: `{
  "action": "receive",
  "from": "9876543210",
  "to": "1001"
}`,
        curl: `curl -X POST ${base}/api/v1/call \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: ${sample}" \\
  -d '{"action":"receive","from":"9876543210","to":"1001"}'`,
      },
      {
        id: "release",
        title: "Release extension",
        method: "POST",
        path: "/api/pbx/extensions/:extension/release",
        summary: "Clear a stuck in-use line.",
        curl: `curl -X POST ${base}/api/pbx/extensions/1001/release \\
  -H "X-Api-Key: ${sample}"`,
      },
    ],
    [base],
  );

  async function viewToken() {
    setBusy(true);
    setError("");
    try {
      const row = await api<LiveToken>("/settings/api-keys/live");
      setRevealed(row.api_key || null);
      setToken({
        id: row.id,
        name: row.name,
        api_key_masked: row.api_key_masked,
        active: row.active,
        created_at: row.created_at,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not view token");
    } finally {
      setBusy(false);
    }
  }

  async function copyToken() {
    setBusy(true);
    setError("");
    try {
      let value = revealed;
      if (!value) {
        const row = await api<LiveToken>("/settings/api-keys/live");
        value = row.api_key || "";
        setRevealed(value);
      }
      if (!value) throw new Error("No token to copy");
      await navigator.clipboard.writeText(value);
      setInfo("Token copied");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not copy");
    } finally {
      setBusy(false);
    }
  }

  async function rotateToken() {
    if (
      !window.confirm(
        "Create a new live token? The current token will be revoked and integrations must use the new key.",
      )
    ) {
      return;
    }
    setBusy(true);
    setError("");
    setInfo("");
    try {
      const row = await api<LiveToken & { api_key: string }>("/settings/api-keys", {
        method: "POST",
        body: JSON.stringify({ name: "Live API token" }),
      });
      setToken({
        id: row.id,
        name: row.name,
        api_key_masked: `${row.api_key.slice(0, 8)}••••••••${row.api_key.slice(-4)}`,
        active: 1,
        created_at: new Date().toISOString(),
      });
      setRevealed(row.api_key);
      setInfo("New live token created. Copy it for your CRM.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not rotate token");
    } finally {
      setBusy(false);
    }
  }

  async function deleteToken() {
    if (!token) return;
    if (
      !window.confirm(
        "Delete the live API token? CRM integrations using this key will stop until you create a new one.",
      )
    ) {
      return;
    }
    setBusy(true);
    setError("");
    setInfo("");
    try {
      await api(`/settings/api-keys/${token.id}`, { method: "DELETE" });
      setToken(null);
      setRevealed(null);
      setInfo("Live token deleted");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete token");
    } finally {
      setBusy(false);
    }
  }

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setInfo("Copied");
    } catch {
      setInfo("Select and copy manually");
    }
  }

  const displayKey = revealed || token?.api_key_masked || "—";

  return (
    <div className="users-page">
      <div className="user-toolbar">
        <div>
          <h1 className="top-oneline">Developers</h1>
          <p className="muted">
            One live API token for CRM integration. Agents dial from their mapped extension.
          </p>
        </div>
      </div>

      <section className="hint-panel">
        <p>
          Header: <code>X-Api-Key: nrn_…</code> · Base URL: <code>{base}</code>
          <br />
          Click-to-call with <code>extension</code> + <code>phoneNumber</code>. Incoming screen-pop:{" "}
          <code>POST /api/v1/crm/screen-pop</code> with <code>phone</code> — <code>{"{name}"}</code> is filled from
          CRM DB / Contacts. Map agents under <b>Users</b> so calls are attributed correctly.
        </p>
      </section>

      {isAdmin ? (
        <section className="panel live-token-panel" style={{ marginTop: 16 }}>
          <div className="live-token-head">
            <div>
              <h3>Live API token</h3>
              <p className="muted live-token-sub">
                {token
                  ? `Created ${token.created_at ? new Date(token.created_at).toLocaleString() : "—"}`
                  : "No token yet — create one for CRM access"}
              </p>
            </div>
            <span className={`pill ${token ? "live" : "dead"}`}>{token ? "Live" : "None"}</span>
          </div>

          {error ? <p className="error">{error}</p> : null}
          {info ? <p className="ok-msg">{info}</p> : null}

          {token ? (
            <div className="live-token-card">
              <div className="live-token-meta">
                <span className="muted">Label</span>
                <b>{token.name}</b>
              </div>
              <div className="live-token-key-row">
                <code className="token-value live-token-key">{displayKey}</code>
              </div>
              <div className="live-token-actions">
                <button
                  type="button"
                  className="btn ghost"
                  disabled={busy}
                  onClick={() => {
                    if (revealed) setRevealed(null);
                    else void viewToken();
                  }}
                >
                  {revealed ? "Hide" : "View"}
                </button>
                <button type="button" className="btn ghost" disabled={busy} onClick={() => void copyToken()}>
                  Copy
                </button>
                <button type="button" className="btn ghost" disabled={busy} onClick={() => void rotateToken()}>
                  Rotate
                </button>
                <button type="button" className="btn danger" disabled={busy} onClick={() => void deleteToken()}>
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <div className="live-token-empty">
              <button className="btn" type="button" disabled={busy} onClick={() => void rotateToken()}>
                Create live token
              </button>
            </div>
          )}
        </section>
      ) : (
        <section className="panel" style={{ marginTop: 16 }}>
          <p className="muted">Ask an admin for the live API token. Reference below.</p>
        </section>
      )}

      <div className="docs-list" style={{ marginTop: 20 }}>
        {docs.map((doc) => (
          <section className="panel docs-endpoint" key={doc.id} id={doc.id}>
            <div className="docs-endpoint-head">
              <span className={`method method-${doc.method.toLowerCase()}`}>{doc.method}</span>
              <code>{doc.path}</code>
            </div>
            <h3>{doc.title}</h3>
            <p className="muted">{doc.summary}</p>
            {doc.body ? (
              <>
                <p className="docs-label">Body</p>
                <pre className="docs-code">{doc.body}</pre>
              </>
            ) : null}
            <div className="docs-code-head">
              <p className="docs-label">cURL</p>
              <button type="button" className="linkish" onClick={() => void copyText(doc.curl)}>
                Copy
              </button>
            </div>
            <pre className="docs-code">{doc.curl}</pre>
          </section>
        ))}
      </div>
    </div>
  );
}
